import { createReducer } from '@reduxjs/toolkit';
import { InfraRequestState, Tenant } from 'TenantModule/types';
import keyBy from 'lodash/keyBy';
import {
  fetchTenants,
  createTenant,
  updateTenant,
  archiveTenant,
  verifyTenant,
  unarchiveTenant,
  decommissionTenant,
  fetchTenantInfraRequest,
  requestTenantInfra,
} from 'TenantModule/thunks';
import { defaultInitialIsoState, DefaultIsoState } from 'IsoStateModule/constants';
import { TenantIsoStateIds } from 'TenantModule/constants';

export type InitialStateType = {
  defaultTenant: Tenant | undefined;
  order: Tenant['id'][];
  tenantHash: Record<Tenant['id'], Tenant>;
  isoStates: Record<string, DefaultIsoState<Tenant['id']>>;
};

export const initialState: InitialStateType = {
  defaultTenant: undefined,
  order: [],
  tenantHash: {},
  isoStates: {},
};

export default createReducer(initialState, (builder) => {
  builder.addCase(fetchTenants.fulfilled, (state, { payload, meta: { arg } }) => {
    const { isoStateId } = arg.meta || {};
    const { tenantIds, defaultTenant } = payload.reduce<{
      tenantIds: number[];
      defaultTenant: InitialStateType['defaultTenant'];
    }>(
      (acc, tenant) => {
        if (tenant.isDefault) {
          acc.defaultTenant = tenant;
        }

        acc.tenantIds.push(tenant.id);

        return acc;
      },
      {
        tenantIds: [],
        defaultTenant: undefined,
      },
    );

    if (isoStateId) {
      // Check if isoState of the isoStateId exists
      const isoState = state.isoStates[isoStateId];
      if (isoState) {
        // update the order
        isoState.topLevelOrder = tenantIds;
      } else {
        const newIsoState = {
          ...defaultInitialIsoState,
          topLevelOrder: tenantIds,
        };

        state.isoStates[isoStateId] = newIsoState;
      }
    } else {
      // Update Global Order
      state.order = tenantIds;
    }

    state.tenantHash = {
      ...state.tenantHash,
      ...keyBy(payload, 'id'),
    };

    if (!state.defaultTenant && defaultTenant) state.defaultTenant = defaultTenant;
  });
  builder.addCase(createTenant.fulfilled, (state, { payload }) => {
    state.tenantHash[payload.id] = payload;
    Object.keys(state.isoStates).forEach((isoStateId) => {
      state.isoStates[isoStateId].topLevelOrder.push(payload.id);
    });
    state.order.push(payload.id);
  });
  builder.addCase(updateTenant.fulfilled, (state, { payload }) => {
    state.tenantHash[payload.id] = payload;
  });
  builder.addCase(archiveTenant.fulfilled, (state, { meta, payload }) => {
    state.tenantHash[payload.id] = payload;

    [TenantIsoStateIds.ActiveTenantTable].forEach((isoStateId) => {
      const isoState = state.isoStates[isoStateId];

      if (isoState) {
        isoState.topLevelOrder = isoState.topLevelOrder.filter(
          (tenantId) => tenantId !== payload.id,
        );
      }
    });
  });
  // Lifecycle transitions (RDM-1409 / GLX-1827): the server returns the updated
  // tenant (status, readinessReport, lastVerifiedAt) — refresh the hash in place.
  // defaultTenant is synced too: useTenants.getTenant serves the default tenant
  // from that field, so a stale copy there would mask the hash update.
  const syncDefaultTenant = (state: InitialStateType, payload: Tenant) => {
    if (state.defaultTenant?.id === payload.id) {
      state.defaultTenant = payload;
    }
  };
  builder.addCase(verifyTenant.fulfilled, (state, { payload }) => {
    state.tenantHash[payload.id] = payload;
    syncDefaultTenant(state, payload);
  });
  builder.addCase(unarchiveTenant.fulfilled, (state, { payload }) => {
    state.tenantHash[payload.id] = payload;
    syncDefaultTenant(state, payload);

    // Mirror of archive: the tenant leaves the archived table view.
    [TenantIsoStateIds.ArchivedTenantTable].forEach((isoStateId) => {
      const isoState = state.isoStates[isoStateId];

      if (isoState) {
        isoState.topLevelOrder = isoState.topLevelOrder.filter(
          (tenantId) => tenantId !== payload.id,
        );
      }
    });
  });
  // Decommissioned tenants stay listed in the archived view with their terminal
  // status — only the hash entry changes.
  builder.addCase(decommissionTenant.fulfilled, (state, { payload }) => {
    state.tenantHash[payload.id] = payload;
    syncDefaultTenant(state, payload);
  });
  // Tenant stack PR requests (RDM-2277): the endpoints return the request, not the
  // tenant, and are addressed by name — patch infraRequest onto the matching tenant.
  const patchInfraRequest = (
    state: InitialStateType,
    tenantName: string,
    infraRequest: InfraRequestState | null,
  ) => {
    Object.values(state.tenantHash).forEach((tenant) => {
      if (tenant.tenantName === tenantName) tenant.infraRequest = infraRequest;
    });
    if (state.defaultTenant?.tenantName === tenantName) {
      state.defaultTenant.infraRequest = infraRequest;
    }
  };
  builder.addCase(fetchTenantInfraRequest.fulfilled, (state, { payload, meta: { arg } }) => {
    patchInfraRequest(state, arg.tenantName, payload.infraRequest);
  });
  builder.addCase(requestTenantInfra.fulfilled, (state, { payload, meta: { arg } }) => {
    patchInfraRequest(state, arg.tenantName, payload);
  });
});
